import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Mail, Eye, Trash2, Edit, Download, AlertCircle } from 'lucide-react';
import { Footer } from '../components/legal';
import { useCookieConsent } from '../context/CookieConsentContext';

const Datenschutz = () => {
  const navigate = useNavigate();
  const { openSettings } = useCookieConsent();

  return (
    <div className="min-h-screen bg-bone font-manrope flex flex-col">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto w-full px-6 md:px-12 py-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate hover:text-navy transition-colors"
            data-testid="datenschutz-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Zurück</span>
          </button>
          <div className="font-playfair text-xl font-bold text-navy">Nico Helfrich</div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-6 md:px-12 py-12">
        <h1 className="font-playfair text-4xl md:text-5xl font-bold text-navy mb-8 tracking-tight">
          Datenschutzerklärung
        </h1>
        
        <div className="bg-white rounded-2xl p-8 md:p-10 border border-black/5 shadow-sm">
          <div className="space-y-8 text-charcoal">
            
            {/* Section 1: Overview */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4">
                1. Datenschutz auf einen Blick
              </h2>
              <h3 className="font-semibold text-navy mb-2">Allgemeine Hinweise</h3>
              <p className="leading-relaxed">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren 
                personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene 
                Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. 
                Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter 
                diesem Text aufgeführten Datenschutzerklärung.
              </p>
            </section>

            {/* Section 2: Data Collection */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4">
                2. Datenerfassung auf dieser Website
              </h2>
              
              <h3 className="font-semibold text-navy mb-2">
                Wer ist verantwortlich für die Datenerfassung auf dieser Website?
              </h3>
              <p className="leading-relaxed mb-4">
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. 
                Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
              </p>

              <h3 className="font-semibold text-navy mb-2">
                Wie erfassen wir Ihre Daten?
              </h3>
              <p className="leading-relaxed mb-4">
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. 
                Hierbei kann es sich z.B. um Daten handeln, die Sie in ein Kontaktformular eingeben 
                oder bei der Nutzung unserer Finanzanalyse angeben.
              </p>
              <p className="leading-relaxed">
                Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website 
                durch unsere IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, 
                Betriebssystem oder Uhrzeit des Seitenaufrufs). Die Erfassung dieser Daten erfolgt 
                automatisch, sobald Sie diese Website betreten.
              </p>
            </section>

            {/* Section 3: Cookies */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4 flex items-center gap-3">
                <Shield className="w-5 h-5 text-emerald" />
                3. Cookies
              </h2>
              
              <h3 className="font-semibold text-navy mb-2">Was sind Cookies?</h3>
              <p className="leading-relaxed mb-4">
                Cookies sind kleine Textdateien, die auf Ihrem Rechner abgelegt werden und die Ihr 
                Browser speichert. Die meisten der von uns verwendeten Cookies sind sogenannte 
                „Session-Cookies". Sie werden nach Ende Ihres Besuchs automatisch gelöscht. 
                Andere Cookies bleiben auf Ihrem Endgerät gespeichert bis Sie diese löschen. 
                Diese Cookies ermöglichen es uns, Ihren Browser beim nächsten Besuch wiederzuerkennen.
              </p>

              <h3 className="font-semibold text-navy mb-2">Welche Cookies verwenden wir?</h3>
              <div className="space-y-4 mb-4">
                <div className="p-4 bg-ice rounded-xl border border-black/5">
                  <h4 className="font-medium text-navy mb-2">Notwendige Cookies</h4>
                  <p className="text-sm leading-relaxed">
                    Diese Cookies sind für die Grundfunktionen der Website erforderlich. 
                    Sie speichern z.B. Ihre Cookie-Präferenzen und ermöglichen die korrekte 
                    Funktionsweise der Website. Diese Cookies können nicht deaktiviert werden.
                  </p>
                </div>
                <div className="p-4 bg-ice rounded-xl border border-black/5">
                  <h4 className="font-medium text-navy mb-2">Statistik / Analyse Cookies</h4>
                  <p className="text-sm leading-relaxed">
                    Diese Cookies helfen uns zu verstehen, wie Besucher unsere Website nutzen. 
                    Die gesammelten Daten sind anonym und dienen ausschließlich der Verbesserung 
                    unseres Angebots. Diese Cookies werden nur mit Ihrer ausdrücklichen 
                    Einwilligung gesetzt.
                  </p>
                </div>
              </div>

              <div className="p-4 bg-emerald/10 rounded-xl border border-emerald/20 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-emerald flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm leading-relaxed text-navy">
                    Sie können Ihre Cookie-Einstellungen jederzeit ändern.
                  </p>
                  <button
                    onClick={openSettings}
                    className="mt-2 text-sm font-medium text-emerald hover:text-emerald/80 transition-colors underline underline-offset-2"
                    data-testid="datenschutz-cookie-settings-btn"
                  >
                    Cookie-Einstellungen öffnen
                  </button>
                </div>
              </div>
            </section>

            {/* Section 4: Analytics Placeholder */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4">
                4. Analyse-Tools
              </h2>
              <p className="leading-relaxed">
                [Platzhalter: Dieser Abschnitt wird ergänzt, sobald ein Analyse-Tool implementiert wird. 
                Aktuell werden keine Analyse-Cookies gesetzt, solange Sie nicht ausdrücklich 
                zugestimmt haben. Die Infrastruktur für eine zukünftige Analyse-Integration ist 
                vorbereitet, aber deaktiviert.]
              </p>
            </section>

            {/* Section 5: GDPR Rights */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4">
                5. Ihre Rechte gemäß DSGVO
              </h2>
              <p className="leading-relaxed mb-6">
                Sie haben jederzeit das Recht auf unentgeltliche Auskunft über Herkunft, Empfänger 
                und Zweck Ihrer gespeicherten personenbezogenen Daten. Sie haben außerdem folgende 
                Rechte bezüglich Ihrer personenbezogenen Daten:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-ice rounded-xl border border-black/5 flex items-start gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Eye className="w-5 h-5 text-cobalt" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Auskunftsrecht</h4>
                    <p className="text-sm text-slate">
                      Recht auf Auskunft über Ihre gespeicherten Daten (Art. 15 DSGVO)
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-ice rounded-xl border border-black/5 flex items-start gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Edit className="w-5 h-5 text-cobalt" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Berichtigungsrecht</h4>
                    <p className="text-sm text-slate">
                      Recht auf Berichtigung unrichtiger Daten (Art. 16 DSGVO)
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-ice rounded-xl border border-black/5 flex items-start gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Trash2 className="w-5 h-5 text-cobalt" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Löschungsrecht</h4>
                    <p className="text-sm text-slate">
                      Recht auf Löschung Ihrer Daten (Art. 17 DSGVO)
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-ice rounded-xl border border-black/5 flex items-start gap-3">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-cobalt" />
                  </div>
                  <div>
                    <h4 className="font-medium text-navy mb-1">Datenübertragbarkeit</h4>
                    <p className="text-sm text-slate">
                      Recht auf Übertragung Ihrer Daten (Art. 20 DSGVO)
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 6: Data Processing */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4">
                6. Datenverarbeitung bei Nutzung der Finanzanalyse
              </h2>
              <p className="leading-relaxed mb-4">
                Bei der Nutzung unserer interaktiven Finanzanalyse werden die von Ihnen eingegebenen 
                Daten (Alter, Einkommen, Sparrate, etc.) zur Berechnung Ihrer persönlichen 
                Finanzsimulation verwendet. Diese Daten werden:
              </p>
              <ul className="list-disc list-inside space-y-2 text-charcoal leading-relaxed">
                <li>Nur für die Dauer der Berechnung verarbeitet</li>
                <li>Nicht dauerhaft auf unseren Servern gespeichert (außer bei E-Mail-Versand)</li>
                <li>Nicht an Dritte weitergegeben</li>
                <li>Ausschließlich zur Erstellung Ihrer personalisierten Analyse verwendet</li>
              </ul>
            </section>

            {/* Section 7: Email */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4 flex items-center gap-3">
                <Mail className="w-5 h-5 text-emerald" />
                7. E-Mail-Kommunikation
              </h2>
              <p className="leading-relaxed">
                Wenn Sie uns Ihre E-Mail-Adresse zur Verfügung stellen, wird diese ausschließlich 
                für den Versand Ihrer Analyseergebnisse und ggf. für die Kontaktaufnahme im Rahmen 
                einer persönlichen Beratung verwendet. Für den E-Mail-Versand nutzen wir den 
                Dienst Resend. Ihre E-Mail-Adresse wird nicht für Marketingzwecke verwendet 
                und nicht an Dritte weitergegeben.
              </p>
            </section>

            {/* Section 8: SSL */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4">
                8. SSL- bzw. TLS-Verschlüsselung
              </h2>
              <p className="leading-relaxed">
                Diese Seite nutzt aus Sicherheitsgründen und zum Schutz der Übertragung 
                vertraulicher Inhalte, wie zum Beispiel Ihrer Finanzdaten, eine SSL- bzw. 
                TLS-Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie daran, 
                dass die Adresszeile des Browsers von „http://" auf „https://" wechselt 
                und an dem Schloss-Symbol in Ihrer Browserzeile.
              </p>
            </section>

            {/* Section 9: Contact for Privacy */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4">
                9. Kontakt für Datenschutzanfragen
              </h2>
              <p className="leading-relaxed mb-4">
                Für Fragen zum Datenschutz, zur Ausübung Ihrer Rechte oder für Beschwerden 
                können Sie sich jederzeit an uns wenden:
              </p>
              <div className="p-4 bg-ice rounded-xl border border-black/5">
                <p className="font-medium text-navy">Nico Helfrich</p>
                <p className="text-slate">E-Mail: nico.helfrich@koenigswege.com</p>
               </div>
            </section>

            {/* Section 10: Updates */}
            <section>
              <h2 className="font-outfit text-xl font-bold text-navy mb-4">
                10. Aktualität und Änderung dieser Datenschutzerklärung
              </h2>
              <p className="leading-relaxed">
                Diese Datenschutzerklärung ist aktuell gültig und hat den Stand: 
                Dezember 2025. Durch die Weiterentwicklung unserer Website und Angebote 
                oder aufgrund geänderter gesetzlicher bzw. behördlicher Vorgaben kann 
                es notwendig werden, diese Datenschutzerklärung zu ändern. Die jeweils 
                aktuelle Datenschutzerklärung kann jederzeit auf dieser Website von 
                Ihnen abgerufen werden.
              </p>
            </section>

          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer variant="light" />
    </div>
  );
};

export default Datenschutz;
